// src/ui/components/parentLockModal.ts — Victorian Brass Lock Modal for Parent Deck Access

import { tokens } from '../designTokens';
import { AuthManager } from '../../auth/authManager';
import { soundEngine } from '@audio/soundEngine';

export interface ParentLockModalOptions {
  onSuccess: () => void;
  onCancel?: () => void;
}

export class ParentLockModal {
  private overlay: HTMLElement;
  private onSuccess: () => void;
  private onCancel?: () => void;

  constructor(options: ParentLockModalOptions) {
    this.onSuccess = options.onSuccess;
    this.onCancel = options.onCancel;
    this.overlay = document.createElement('div');
    this.overlay.id = 'parent-lock-modal-overlay';
    this.overlay.className = 'parent-lock-overlay';
    this.render();
  }

  show(): void {
    document.body.appendChild(this.overlay);
    const input = this.overlay.querySelector<HTMLInputElement>('#parent-password-input');
    input?.focus();
  }

  close(): void {
    if (document.body.contains(this.overlay)) {
      document.body.removeChild(this.overlay);
    }
  }

  private render(): void {
    this.overlay.style.cssText = `
      position: fixed;
      inset: 0;
      background: rgba(7, 20, 36, 0.72);
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 99999;
      padding: 16px;
    `;

    this.overlay.innerHTML = `
      <div
        class="morris-card"
        style="
          max-width: 440px;
          width: 100%;
          background: #FAF8F5;
          border: 3px double ${tokens.color.gildedBrass};
          border-radius: 20px;
          padding: 32px 28px;
          box-shadow: 0 16px 40px rgba(7, 20, 36, 0.35);
          text-align: center;
          position: relative;
          animation: popIn 0.25s ease-out;
        "
      >
        <button
          id="close-lock-btn"
          aria-label="Close"
          style="
            position: absolute;
            top: 14px;
            right: 14px;
            background: none;
            border: none;
            font-size: 1.3rem;
            color: ${tokens.color.morrisIndigo};
            cursor: pointer;
            padding: 4px 8px;
          "
        >✕</button>

        <div style="font-size: 2.8rem; margin-bottom: 8px;">🔐</div>

        <div style="font-size: 0.8rem; color: ${tokens.color.seaweedSage}; font-weight: 800; text-transform: uppercase; letter-spacing: 0.08em;">
          Parent &amp; Educator Observatory
        </div>

        <h3 style="font-size: 1.45rem; color: ${tokens.color.morrisIndigo}; margin: 8px 0 12px; font-family: 'Cinzel', serif;">
          Parent Deck Locked
        </h3>

        <p style="font-size: 0.92rem; color: #3A4D66; margin: 0 0 20px; line-height: 1.45;">
          Enter your parent observatory key to inspect Tudor's SAS trajectories, cognitive fatigue curves, and session limits.
        </p>

        <form id="parent-lock-form" style="display: flex; flex-direction: column; gap: 14px;">
          <input
            id="parent-password-input"
            type="password"
            placeholder="Parent Observatory Key"
            autocomplete="current-password"
            required
            class="touch-target"
            style="
              width: 100%;
              padding: 12px 16px;
              font-size: 1.05rem;
              border: 1.5px solid ${tokens.color.gildedBrass};
              border-radius: 12px;
              background: #FFFDF9;
              color: ${tokens.color.morrisIndigo};
              box-sizing: border-box;
            "
          />

          <div style="display: flex; align-items: center; gap: 8px; text-align: left; font-size: 0.85rem; color: #4A5B70;">
            <input type="checkbox" id="remember-parent-device" checked style="cursor: pointer;" />
            <label for="remember-parent-device" style="cursor: pointer;">Remember this device for 30 days</label>
          </div>

          <div id="parent-lock-error" style="display: none; color: ${tokens.color.madderRed}; font-size: 0.85rem; font-weight: 700;"></div>

          <button
            type="submit"
            id="unlock-submit-btn"
            class="touch-target"
            style="
              width: 100%;
              padding: 14px;
              background: ${tokens.color.morrisIndigo};
              color: #FAF8F5;
              border: 2px solid ${tokens.color.gildedBrass};
              border-radius: 12px;
              font-size: 1rem;
              font-weight: 800;
              cursor: pointer;
              transition: transform 0.15s ease, background 0.15s ease;
            "
          >
            Unlock Parent Deck &rarr;
          </button>
        </form>

        <div style="margin-top: 16px; font-size: 0.8rem; color: #7A8B9E;">
          First visit? Enter your chosen master password to establish your parental key.
        </div>
      </div>
    `;

    // Wire close / cancel
    this.overlay.querySelector('#close-lock-btn')?.addEventListener('click', () => {
      this.close();
      if (this.onCancel) this.onCancel();
    });

    // Wire form submit
    const form = this.overlay.querySelector<HTMLFormElement>('#parent-lock-form');
    const input = this.overlay.querySelector<HTMLInputElement>('#parent-password-input');
    const rememberBox = this.overlay.querySelector<HTMLInputElement>('#remember-parent-device');
    const errorDiv = this.overlay.querySelector<HTMLElement>('#parent-lock-error');
    const submitBtn = this.overlay.querySelector<HTMLButtonElement>('#unlock-submit-btn');

    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const secret = input?.value.trim() || '';
      if (!secret) return;

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Verifying...';
      }
      if (errorDiv) {
        errorDiv.style.display = 'none';
      }

      // Try login
      const result = await AuthManager.login({
        role: 'parent',
        secret,
        rememberMe: rememberBox?.checked,
      });

      // If backend returns isFirstRunSetup, set it up automatically
      if (!result.success && result.isFirstRunSetup) {
        const setupResult = await AuthManager.setupParent({ parentPassword: secret });
        if (setupResult.success) {
          soundEngine.playSuccess();
          this.close();
          this.onSuccess();
          return;
        }
      }

      if (result.success) {
        soundEngine.playSuccess();
        this.close();
        this.onSuccess();
      } else {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = 'Unlock Parent Deck →';
        }
        if (errorDiv) {
          errorDiv.textContent = result.error || 'Incorrect key. Please try again.';
          errorDiv.style.display = 'block';
        }
      }
    });
  }
}
